
import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { getCategoriesWithCounts, Category } from '@/data/categories';
import { categorySlugMap } from '@/data/categoryPages';

const CategoryCarousel = () => {
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();
  const [categories, setCategories] = React.useState<(Category & { venueCount: number })[]>([]);

  React.useEffect(() => {
    getCategoriesWithCounts().then((cats) =>
      setCategories(
        cats.filter((c) => c.venueCount > 0).sort((a, b) => b.venueCount - a.venueCount)
      )
    );
  }, []);

  return (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-4">
      {/* Section header */}
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <h2 className="font-heading font-extrabold text-brand-dark tracking-tight leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 40px)', letterSpacing: '-0.02em' }}>
            Trouve ton kif <em className="not-italic text-brand-primary">par catégorie</em>
          </h2>
          <p className="text-brand-text-muted mt-2 text-base">Du bowling tranquille au karting qui décoiffe, il y en a pour toutes les ambiances.</p>
        </div>
        <Link to="/search" className="text-brand-text font-semibold text-sm hover:text-brand-primary transition-colors flex-shrink-0">
          Toutes les catégories →
        </Link>
      </div>

      {/* Mobile: horizontal swipe carousel. sm+: grid or flex-wrap */}
      <div
        className={[
          'gap-3.5',
          // Mobile: snap-scroll carousel
          'flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2 scrollbar-none',
          // sm+: revert to grid or centered flex
          categories.length >= 6
            ? 'sm:grid sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0'
            : 'sm:flex-wrap sm:justify-center sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0',
        ].join(' ')}
      >
        {categories.map((category, i) => {
          const to = categorySlugMap[category.id]
            ? `/${categorySlugMap[category.id]}`
            : `/search?category=${category.id}`;
          return (
            <motion.div
              key={category.id}
              initial={shouldReduce ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.4, delay: i * 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`snap-start shrink-0 w-[45%] min-w-[140px] ${categories.length < 6 ? 'sm:w-48' : 'sm:w-auto'}`}
            >
              <div
                onClick={() => navigate(to)}
                className="group relative cursor-pointer rounded-xl overflow-hidden shadow-brand-sm hover:shadow-brand-md hover:scale-[1.025] transition-all duration-250"
                style={{ aspectRatio: '0.9/1' }}
              >
                {/* Background image */}
                <img
                  src={category.imageUrl}
                  alt={category.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-55 group-hover:opacity-65 transition-opacity duration-250`} />

                {/* Count badge */}
                <span className="absolute top-3 left-3 bg-white/92 text-brand-text rounded-pill px-2.5 py-1 text-[11px] font-semibold">
                  {category.venueCount} lieu{category.venueCount > 1 ? 'x' : ''}
                </span>

                {/* Label */}
                <div className="absolute left-3.5 right-3.5 bottom-3.5 text-white group-hover:-translate-y-1 transition-transform duration-250">
                  <div className="text-[28px]" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.4))' }}>{category.icon}</div>
                  <div className="font-heading font-bold text-[17px] mt-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,.6)' }}>{category.label}</div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* SEO paragraph */}
      <p className="text-center text-sm text-brand-text-muted leading-relaxed mt-10 max-w-3xl mx-auto">
        FullBook est la plateforme de référence pour réserver vos{' '}
        <strong>activités indoor en Île-de-France</strong>.{' '}
        Que vous cherchiez un{' '}
        <Link to="/escape-game-paris" className="text-brand-primary hover:underline font-medium">escape game à Paris</Link>,{' '}
        une soirée <Link to="/bowling-paris" className="text-brand-primary hover:underline font-medium">bowling</Link>, du{' '}
        <Link to="/karting-paris" className="text-brand-primary hover:underline font-medium">karting indoor</Link>{' '}
        ou une expérience <Link to="/vr-arcade-paris" className="text-brand-primary hover:underline font-medium">VR arcade</Link> :
        toutes les disponibilités s'affichent en temps réel et la réservation se fait en quelques clics.
      </p>
    </section>
  );
};

export default CategoryCarousel;
